import type { SVGProps } from "react";

/**
 * The app's icon set: hand-drawn line glyphs, no icon package.
 *
 * lucide/heroicons would be a dependency and a build-time tree-shake for the
 * seven marks this app actually uses; the project's rule is a deliberate, small
 * dependency list. Everything here is stroke-only on a 24x24 grid, sized by the
 * caller through `className` and coloured by `currentColor`, so a nav item's
 * active/muted state reaches the icon without a single colour prop.
 */
export type IconProps = Omit<SVGProps<SVGSVGElement>, "children">;

/** Shared geometry. 1.6 keeps the strokes crisp at the 18-20px the nav uses. */
const BASE = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.6,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
  focusable: false,
} as const;

/** Сьогодні — a calendar with today marked. */
export function IconToday(props: IconProps) {
  return (
    <svg {...BASE} {...props}>
      <rect x="3" y="4.75" width="18" height="16" rx="2.5" />
      <path d="M8 2.75v4M16 2.75v4M3 9.75h18" />
      <circle cx="12" cy="15" r="1.6" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** Фінанси — a wallet with the card slot. */
export function IconFinance(props: IconProps) {
  return (
    <svg {...BASE} {...props}>
      <path d="M3 6.6A2.6 2.6 0 0 1 5.6 4H17.4v3.4" />
      <path d="M3 6.6V18a2.4 2.4 0 0 0 2.4 2.4h13.2A2.4 2.4 0 0 0 21 18v-7.6a2.4 2.4 0 0 0-2.4-2.4H5.6A2.6 2.6 0 0 1 3 6.6Z" />
      <circle cx="16.8" cy="14.2" r="1.15" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** Бізнес — a storefront: awning, body, door. */
export function IconBusiness(props: IconProps) {
  return (
    <svg {...BASE} {...props}>
      <path d="M4.7 3.5h14.6L21 9.2H3l1.7-5.7Z" />
      <path d="M4.4 9.2V19a1.5 1.5 0 0 0 1.5 1.5h12.2a1.5 1.5 0 0 0 1.5-1.5V9.2" />
      <path d="M9.6 20.5v-6h4.8v6" />
    </svg>
  );
}

/** Цілі — the target. */
export function IconGoals(props: IconProps) {
  return (
    <svg {...BASE} {...props}>
      <circle cx="12" cy="12" r="8.6" />
      <circle cx="12" cy="12" r="4.4" />
      <circle cx="12" cy="12" r="1.3" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** Здоровʼя — a heart with the pulse line across it. */
export function IconHealth(props: IconProps) {
  return (
    <svg {...BASE} {...props}>
      <path d="M12 20.4 4.6 13.1a4.9 4.9 0 0 1-.2-6.8 4.7 4.7 0 0 1 7 .3l.6.7.6-.7a4.7 4.7 0 0 1 7-.3 4.9 4.9 0 0 1-.2 6.8L12 20.4Z" />
      <path d="M2.9 12.4h4.3l1.5-2.9 2.3 5.7 1.7-3.6 1 .8h5.4" />
    </svg>
  );
}

/** Journaling — an open book, spine down the middle. */
export function IconJournal(props: IconProps) {
  return (
    <svg {...BASE} {...props}>
      <path d="M12 6.9C10.3 5.4 7.9 4.7 4.7 4.9a1.1 1.1 0 0 0-1 1.1v11.3a1.1 1.1 0 0 0 1.1 1.1c3-.2 5.3.4 7.2 1.7 1.9-1.3 4.2-1.9 7.2-1.7a1.1 1.1 0 0 0 1.1-1.1V6a1.1 1.1 0 0 0-1-1.1c-3.2-.2-5.6.5-7.3 2Z" />
      <path d="M12 6.9v13.2" />
    </svg>
  );
}

/** Зошити — a closed notebook: spine band on the left, a label on the cover. */
export function IconNotebooks(props: IconProps) {
  return (
    <svg {...BASE} {...props}>
      <rect x="5" y="3.4" width="14" height="17.2" rx="1.8" />
      <path d="M8.6 3.4v17.2" />
      <path d="M11.4 8.2h4.6M11.4 11.2h3.2" />
    </svg>
  );
}

/** Нотатки — a sheet with its corner turned, two lines written on it. */
export function IconNotes(props: IconProps) {
  return (
    <svg {...BASE} {...props}>
      <path d="M6.4 3.6h6.4l5 5v11a1.1 1.1 0 0 1-1.1 1.1H6.4a1.1 1.1 0 0 1-1.1-1.1V4.7a1.1 1.1 0 0 1 1.1-1.1Z" />
      <path d="M12.8 3.6v4a1.1 1.1 0 0 0 1.1 1.1h3.9" />
      <path d="M8.4 13.1h7.2M8.4 16.5h4.6" />
    </svg>
  );
}

/* --- The four ways a goal can be marked done ------------------------------ */

/** Галочка. */
export function IconCheck(props: IconProps) {
  return (
    <svg {...BASE} {...props}>
      <path d="m4.5 12.6 4.8 4.8L19.5 6.6" />
    </svg>
  );
}

/** Число. */
export function IconNumber(props: IconProps) {
  return (
    <svg {...BASE} {...props}>
      <path d="M4.5 9.2h15M4.5 14.8h15M10.2 3.6 8.2 20.4M16.2 3.6l-2 16.8" />
    </svg>
  );
}

/** Відсоток. */
export function IconPercent(props: IconProps) {
  return (
    <svg {...BASE} {...props}>
      <path d="M18.6 5.4 5.4 18.6" />
      <circle cx="7.6" cy="7.6" r="2.7" />
      <circle cx="16.4" cy="16.4" r="2.7" />
    </svg>
  );
}

/** Підзадачі. */
export function IconChecklist(props: IconProps) {
  return (
    <svg {...BASE} {...props}>
      <path d="m3.4 6.1 1.7 1.7 3.1-3.5" />
      <path d="m3.4 13.6 1.7 1.7 3.1-3.5" />
      <path d="M3.9 19.9h3.9" />
      <path d="M11.6 5.9h9M11.6 13.2h9M11.6 19.9h9" />
    </svg>
  );
}

/* --- The lifecycle of a note ---------------------------------------------- */

/** Порожнє коло — думка ще в роботі. The one control that completes a note. */
export function IconCircle(props: IconProps) {
  return (
    <svg {...BASE} {...props}>
      <circle cx="12" cy="12" r="8.4" />
    </svg>
  );
}

/** Коло з галочкою — виконано. */
export function IconCheckCircle(props: IconProps) {
  return (
    <svg {...BASE} {...props}>
      <circle cx="12" cy="12" r="8.4" />
      <path d="m8.3 12.2 2.6 2.6 4.8-5.2" />
    </svg>
  );
}

/**
 * Календар — the date a note was put off until.
 *
 * `IconToday` is a calendar too, but it carries the dot that means "today";
 * on a 12px badge that dot is a smudge, and it would be saying the wrong thing.
 */
export function IconCalendar(props: IconProps) {
  return (
    <svg {...BASE} {...props}>
      <rect x="3.4" y="5" width="17.2" height="15.6" rx="2.5" />
      <path d="M8 2.9v4M16 2.9v4M3.4 10h17.2" />
    </svg>
  );
}

/**
 * Відеозустріч — a camera body with its lens barrel.
 *
 * The one mark that separates a call from an appointment in «Найближчі події»:
 * an event with a Meet link is somewhere you open a tab, not somewhere you go.
 */
export function IconVideo(props: IconProps) {
  return (
    <svg {...BASE} {...props}>
      <rect x="2.8" y="6.4" width="12.6" height="11.2" rx="2.4" />
      <path d="m15.4 11.2 5.8-3.2v8l-5.8-3.2Z" />
    </svg>
  );
}

/** Chevron left — «попередній день» on the day grid. */
export function IconChevronLeft(props: IconProps) {
  return (
    <svg {...BASE} {...props}>
      <path d="m14.4 5.6-6.4 6.4 6.4 6.4" />
    </svg>
  );
}

/** Chevron right — «наступний день». */
export function IconChevronRight(props: IconProps) {
  return (
    <svg {...BASE} {...props}>
      <path d="m9.6 5.6 6.4 6.4-6.4 6.4" />
    </svg>
  );
}

/**
 * Two arrows pushing apart into opposite corners — «На весь екран» on the day
 * grid. The corner brackets are what make it read as *the frame growing*
 * rather than as a generic diagonal; {@link IconCollapse} is the same drawing
 * run backwards, so the pair reads as one switch.
 */
export function IconExpand(props: IconProps) {
  return (
    <svg {...BASE} {...props}>
      <path d="M14.5 3.5H20.5V9.5" />
      <path d="M9.5 20.5H3.5V14.5" />
      <path d="M20.5 3.5 13.6 10.4" />
      <path d="M3.5 20.5 10.4 13.6" />
    </svg>
  );
}

/** The same two arrows pulled back in — «Згорнути» on the week overlay. */
export function IconCollapse(props: IconProps) {
  return (
    <svg {...BASE} {...props}>
      <path d="M20.5 10.5H14.5V4.5" />
      <path d="M3.5 13.5H9.5V19.5" />
      <path d="M21 4 14.5 10.5" />
      <path d="M3 20 9.5 13.5" />
    </svg>
  );
}

/** A closed padlock — the Journaling gate, locked and lock-now alike. */
export function IconLock(props: IconProps) {
  return (
    <svg {...BASE} {...props}>
      <rect x="4.6" y="10.4" width="14.8" height="9.6" rx="2.2" />
      <path d="M8.2 10.4V7.9a3.8 3.8 0 0 1 7.6 0v2.5" />
      <path d="M12 14.1v2.3" />
    </svg>
  );
}

/** Backspace — a delete key body with an X, for the journal PIN keypad. */
export function IconBackspace(props: IconProps) {
  return (
    <svg {...BASE} {...props}>
      <path d="M9.2 5.6h9.4a1.6 1.6 0 0 1 1.6 1.6v9.6a1.6 1.6 0 0 1-1.6 1.6H9.2a1.6 1.6 0 0 1-1.24-.59L3.4 12l4.56-5.81a1.6 1.6 0 0 1 1.24-.59Z" />
      <path d="M12.4 9.6l4 4.8M16.4 9.6l-4 4.8" />
    </svg>
  );
}

/**
 * The burger, which is the one icon that is not a static glyph: three bars that
 * fold into a cross.
 *
 * Three positioned bars rather than an SVG because a `rotate` on an SVG line
 * needs `transform-box: fill-box` to spin around its own centre, and Safari has
 * historically been inconsistent about that; a `<span>` rotates about its own
 * box everywhere. The middle bar collapses to nothing first (150ms) so the
 * outer two rotate into a clean X rather than through it.
 */
export function BurgerIcon({ open }: { open: boolean }) {
  const bar =
    "absolute left-0 h-[1.6px] w-full rounded-full bg-current transition-all duration-300 ease-drawer";
  return (
    <span aria-hidden="true" className="relative block h-3.5 w-[18px]">
      <span
        className={`${bar} top-0 ${open ? "translate-y-[6.2px] rotate-45" : ""}`}
      />
      <span
        className={`${bar} top-[6.2px] duration-150 ${open ? "scale-x-0 opacity-0" : "opacity-100"}`}
      />
      <span
        className={`${bar} top-[12.4px] ${open ? "-translate-y-[6.2px] -rotate-45" : ""}`}
      />
    </span>
  );
}

/* --- Trend against a baseline --------------------------------------------- */

/**
 * Trend arrows. Three glyphs rather than one rotated mark: the flat case is a
 * bar, not a turned arrow, and a `rotate` on a shared icon would have to spin
 * about its own centre — the same `transform-box` trap `BurgerIcon` avoids.
 *
 * Direction is the *movement* (higher / lower), never the verdict; the colour
 * carries whether that movement is good, because for stress down is up.
 */
export function IconTrendUp(props: IconProps) {
  return (
    <svg {...BASE} {...props}>
      <path d="M12 19.4V5.2" />
      <path d="m6.4 10.8 5.6-5.6 5.6 5.6" />
    </svg>
  );
}

/** Trend arrow, downwards. */
export function IconTrendDown(props: IconProps) {
  return (
    <svg {...BASE} {...props}>
      <path d="M12 4.6v14.2" />
      <path d="m6.4 13.2 5.6 5.6 5.6-5.6" />
    </svg>
  );
}

/** No movement worth reporting — or nothing to compare against. */
export function IconTrendFlat(props: IconProps) {
  return (
    <svg {...BASE} {...props}>
      <path d="M5 12h14" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Notebook tools («Зошити»)
// ---------------------------------------------------------------------------

/** Plus — add a notebook, add a page. */
export function IconPlus(props: IconProps) {
  return (
    <svg {...BASE} {...props}>
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

export function IconMinus(props: IconProps) {
  return (
    <svg {...BASE} {...props}>
      <path d="M5 12h14" />
    </svg>
  );
}

/** Текст — a serif T. */
export function IconText(props: IconProps) {
  return (
    <svg {...BASE} {...props}>
      <path d="M5 6.5V5h14v1.5M12 5v14M9.5 19h5" />
    </svg>
  );
}

/** Ручка — a pen nib drawn at an angle. */
export function IconPen(props: IconProps) {
  return (
    <svg {...BASE} {...props}>
      <path d="M15.6 4.6a2 2 0 0 1 2.8 0l1 1a2 2 0 0 1 0 2.8L8.2 19.6 4 20l.4-4.2Z" />
      <path d="m13.8 6.4 3.8 3.8" />
    </svg>
  );
}

/** Маркер — a chisel-tip highlighter over a stroke of colour. */
export function IconMarker(props: IconProps) {
  return (
    <svg {...BASE} {...props}>
      <path d="m14.5 4.5 5 5-7.3 7.3H7.2v-5Z" />
      <path d="m7.2 16.8-2.7 2.7h4.3" />
    </svg>
  );
}

/** Гумка — an eraser block. */
export function IconEraser(props: IconProps) {
  return (
    <svg {...BASE} {...props}>
      <path d="m14 4.8 5.2 5.2a1.6 1.6 0 0 1 0 2.3L12.5 19H8.3l-3.5-3.5a1.6 1.6 0 0 1 0-2.3l6.9-6.9" />
      <path d="M9.2 9.8 14.2 15M12.5 19H20" />
    </svg>
  );
}

/** Лінія. */
export function IconLine(props: IconProps) {
  return (
    <svg {...BASE} {...props}>
      <path d="M5 19 19 5" />
    </svg>
  );
}

/** Стрілка. */
export function IconArrow(props: IconProps) {
  return (
    <svg {...BASE} {...props}>
      <path d="M5 19 19 5M10 5h9v9" />
    </svg>
  );
}

/** Прямокутник. */
export function IconRect(props: IconProps) {
  return (
    <svg {...BASE} {...props}>
      <rect x="4.5" y="6" width="15" height="12" rx="0.8" />
    </svg>
  );
}

/** Коло. */
export function IconEllipse(props: IconProps) {
  return (
    <svg {...BASE} {...props}>
      <circle cx="12" cy="12" r="7.5" />
    </svg>
  );
}

/** Скасувати. */
export function IconUndo(props: IconProps) {
  return (
    <svg {...BASE} {...props}>
      <path d="M9 14 4.5 9.5 9 5" />
      <path d="M4.5 9.5H14a5.5 5.5 0 0 1 0 11h-3" />
    </svg>
  );
}

/** Повторити. */
export function IconRedo(props: IconProps) {
  return (
    <svg {...BASE} {...props}>
      <path d="m15 14 4.5-4.5L15 5" />
      <path d="M19.5 9.5H10a5.5 5.5 0 0 0 0 11h3" />
    </svg>
  );
}
